import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ArrowLeft, MessageCircle, Sparkles, Pencil, ShieldCheck, Trash2, ThumbsUp, Heart, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

export default function CharacterDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [character, setCharacter] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [showStart, setShowStart] = useState(false);
  const [chosenPersona, setChosenPersona] = useState("");
  const [chatStyle, setChatStyle] = useState(""); // "" = user hasn't picked; can pick later inside the chat
  const [starting, setStarting] = useState(false);
  const [engBusy, setEngBusy] = useState(false);

  async function toggleLike() {
    if (!character || engBusy) return;
    setEngBusy(true);
    const nextLiked = !character.is_liked;
    setCharacter((c) => ({ ...c, is_liked: nextLiked, like_count: (c.like_count || 0) + (nextLiked ? 1 : -1) }));
    try {
      const { data } = nextLiked
        ? await api.post(`/characters/${character.id}/like`)
        : await api.delete(`/characters/${character.id}/like`);
      setCharacter((c) => ({ ...c, is_liked: !!data.is_liked, like_count: data.like_count }));
    } catch (e) {
      setCharacter((c) => ({ ...c, is_liked: !nextLiked, like_count: (c.like_count || 0) + (nextLiked ? -1 : 1) }));
      toast.error(e.response?.data?.detail || "Could not update like.");
    } finally { setEngBusy(false); }
  }

  async function toggleFavorite() {
    if (!character || engBusy) return;
    setEngBusy(true);
    const nextFav = !character.is_favorited;
    setCharacter((c) => ({ ...c, is_favorited: nextFav }));
    try {
      const { data } = nextFav
        ? await api.post(`/characters/${character.id}/favorite`)
        : await api.delete(`/characters/${character.id}/favorite`);
      setCharacter((c) => ({ ...c, is_favorited: !!data.is_favorited }));
      if (nextFav) toast.success("Added to favorites.");
    } catch (e) {
      setCharacter((c) => ({ ...c, is_favorited: !nextFav }));
      toast.error(e.response?.data?.detail || "Could not update favorite.");
    } finally { setEngBusy(false); }
  }

  useEffect(() => {
    api.get(`/characters/${id}`).then(({ data }) => {
      setCharacter(data);
      setChatStyle("");
    });
    api.get("/personas").then(({ data }) => {
      setPersonas(data.personas);
      const def = data.personas.find((p) => p.is_default);
      if (def) setChosenPersona(def.id);
    });
  }, [id]);

  async function startChat() {
    setStarting(true);
    try {
      const { data } = await api.post("/chats", {
        character_id: id,
        persona_id: chosenPersona || null,
        chat_style: chatStyle || null,
      });
      nav(`/chat/${data.id}`, { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start chat.");
    } finally {
      setStarting(false);
    }
  }

  if (!character) {
    return <div className="p-10 text-center text-neutral-500">Loading…</div>;
  }

  return (
    <div className="min-h-screen">
      <div className="relative">
        {character.avatar_url ? (
          <div className="relative w-full overflow-hidden bg-black">
            {/* Fully-viewable hero: use object-contain so the whole portrait is
                visible (no top/bottom crop). Cap the height so it doesn't dominate
                on very tall images. */}
            <img
              src={character.avatar_url}
              alt={character.name}
              className="mx-auto block max-h-[70vh] w-auto max-w-full object-contain"
              data-testid="detail-hero-image"
            />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#0B0A10] via-[#0B0A10]/60 to-transparent" />
          </div>
        ) : (
          <div className="h-[35vh] bg-gradient-to-br from-violet-900 to-neutral-900" />
        )}

        <button
          data-testid="detail-back-btn"
          onClick={() => nav(-1)}
          className="absolute left-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-black/40 backdrop-blur text-white"
        >
          <ArrowLeft size={18} />
        </button>
        {character && character.creator_id && character.creator_id === user?.id && (
          <button
            data-testid="detail-edit-btn"
            onClick={() => nav(`/edit-character/${character.id}`)}
            className="absolute right-4 top-4 flex h-10 items-center gap-1.5 rounded-full bg-black/40 backdrop-blur px-3 text-xs text-white hover:bg-black/60"
          >
            <Pencil size={12} /> Edit
          </button>
        )}
        {/* Admin-only delete: appears for any character, including ones the admin didn't create. */}
        {character && user?.is_admin && character.creator_id !== user.id && (
          <button
            data-testid="detail-admin-delete-btn"
            onClick={async () => {
              if (!confirm(`ADMIN: Permanently delete "${character.name}" (created by @${character.creator_username || "unknown"})? This is logged.`)) return;
              try {
                await api.delete(`/characters/${character.id}`);
                toast.success("Character deleted (admin action logged).");
                nav("/discover", { replace: true });
              } catch (e) {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed.");
              }
            }}
            className="absolute right-4 top-4 flex h-10 items-center gap-1.5 rounded-full bg-rose-500/20 backdrop-blur px-3 text-xs text-rose-100 border border-rose-500/40 hover:bg-rose-500/30"
          >
            <ShieldCheck size={12} /> <Trash2 size={12} /> Admin delete
          </button>
        )}
      </div>

      <div className="mx-auto -mt-16 max-w-2xl px-5 pb-40 relative">
        <div className="flex items-center gap-2">
          {character.is_nsfw ? (
            <span className="nsfw-badge" data-testid="detail-nsfw-badge">18+</span>
          ) : (
            <span className="sfw-badge">SFW</span>
          )}
          {character.tags?.map((t) => (
            <span key={t} className="rounded-full bg-white/5 border border-white/10 px-2.5 py-0.5 text-[10px] uppercase tracking-widest text-neutral-400">#{t}</span>
          ))}
        </div>
        <h1 className="font-display mt-3 text-4xl leading-tight text-white">{character.name}</h1>
        {character.tagline && <p className="mt-2 text-neutral-300 leading-relaxed">{character.tagline}</p>}
        {character.creator_username && (
          <p className="mt-1 text-xs text-neutral-500">
            by{" "}
            {character.creator_id && character.creator_id !== "system" ? (
              <Link
                to={`/u/${character.creator_username}`}
                data-testid="detail-creator-link"
                className="text-violet-300 hover:text-violet-200"
              >
                @{character.creator_username}
              </Link>
            ) : (
              <>@{character.creator_username}</>
            )}
          </p>
        )}

        {/* Engagement — Like (public upvote, drives Trending) + Favorite (private save). */}
        <div className="mt-4 flex items-center gap-2">
          <button
            data-testid="detail-like-btn"
            onClick={toggleLike}
            disabled={engBusy}
            className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-xs transition ${
              character.is_liked
                ? "bg-violet-500 text-white"
                : "border border-white/10 bg-white/5 text-neutral-200 hover:bg-white/10"
            }`}
          >
            <ThumbsUp size={14} fill={character.is_liked ? "currentColor" : "none"} />
            <span data-testid="detail-like-count">{character.like_count || 0}</span>
            <span className="opacity-80">{character.is_liked ? "Liked" : "Like"}</span>
          </button>
          <button
            data-testid="detail-favorite-btn"
            onClick={toggleFavorite}
            disabled={engBusy}
            className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-xs transition ${
              character.is_favorited
                ? "bg-rose-500 text-white"
                : "border border-white/10 bg-white/5 text-neutral-200 hover:bg-white/10"
            }`}
          >
            <Heart size={14} fill={character.is_favorited ? "currentColor" : "none"} />
            {character.is_favorited ? "Favorited" : "Favorite"}
          </button>
        </div>

        <div className="mt-6 space-y-4">
          {character.description && <InfoBlock title="Description" body={character.description} />}
        </div>

        <button
          data-testid="detail-start-chat-btn"
          onClick={() => setShowStart(true)}
          className="btn-pill btn-primary fixed bottom-6 left-1/2 z-30 -translate-x-1/2 px-8 py-3.5 shadow-2xl"
        >
          <MessageCircle size={18} />
          Start chat
        </button>
      </div>

      <Dialog open={showStart} onOpenChange={setShowStart}>
        <DialogContent className="glass-strong border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-white">Start a story</DialogTitle>
          </DialogHeader>

          <div className="mt-2 space-y-4">
            <div>
              <div className="mb-2 text-xs uppercase tracking-widest text-neutral-500">Persona</div>
              {/* Compact dropdown — closed trigger shows selected persona's
                  avatar + name; open list shows all personas with avatars so
                  users can see who they're picking. Keeps the modal
                  short enough for mobile. */}
              <Select value={chosenPersona || "__none__"} onValueChange={(v) => setChosenPersona(v === "__none__" ? "" : v)}>
                <SelectTrigger
                  data-testid="start-persona-select"
                  className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 h-auto text-white outline-none focus:border-violet-500/50 focus:ring-0 [&>span]:flex [&>span]:items-center [&>span]:gap-2 [&>span]:min-w-0"
                >
                  <SelectValue placeholder="No persona (default)" />
                </SelectTrigger>
                <SelectContent className="glass-strong border-white/10 text-white">
                  <SelectItem value="__none__" data-testid="start-persona-opt-none" className="focus:bg-violet-500/15 focus:text-white">
                    <div className="flex items-center gap-2">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-neutral-800">
                        <UserIcon size={12} className="text-neutral-400" />
                      </div>
                      <span className="text-sm">No persona</span>
                    </div>
                  </SelectItem>
                  {personas.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`start-persona-opt-${p.id}`} className="focus:bg-violet-500/15 focus:text-white">
                      <div className="flex items-center gap-2">
                        {p.avatar_url ? (
                          <img src={p.avatar_url} alt="" className="h-7 w-7 shrink-0 rounded-full object-cover" />
                        ) : (
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-500/20 text-[11px] font-display text-violet-200">
                            {(p.name?.[0] || "?").toUpperCase()}
                          </div>
                        )}
                        <span className="text-sm">{p.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                onClick={() => nav("/personas")}
                className="mt-1.5 text-xs text-violet-300 hover:text-violet-200"
                data-testid="start-manage-personas-btn"
              >
                Manage personas →
              </button>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-neutral-500">Chat style</div>
                <div className="text-[10px] text-neutral-500">Optional · pick later too</div>
              </div>
              <div className="grid grid-cols-2 gap-2" data-testid="start-style-list">
                {[
                  { id: "fluff",     title: "Fluff",             desc: "Cozy, warm, low-stakes." },
                  { id: "slowburn",  title: "Slow-burn Romance", desc: "Longing, tender pacing." },
                  { id: "drama",     title: "Angst / Drama",     desc: "Emotional stakes, conflict." },
                  { id: "comedy",    title: "Comedy",            desc: "Playful, witty banter." },
                  { id: "adventure", title: "Adventure",         desc: "Action, quests, energy." },
                  { id: "dark",      title: "Dark / Thriller",   desc: "Suspense and edge." },
                  { id: "grounded",  title: "Grounded",          desc: "Serious, character-driven." },
                ].map((s) => (
                  <StyleOption
                    key={s.id}
                    active={chatStyle === s.id}
                    onSelect={() => setChatStyle(chatStyle === s.id ? "" : s.id)}
                    testId={`start-style-${s.id}`}
                    title={s.title}
                    desc={s.desc}
                  />
                ))}
              </div>
            </div>

            <button
              data-testid="start-confirm-btn"
              disabled={starting}
              onClick={startChat}
              className="btn-pill btn-primary w-full py-3 disabled:opacity-50"
            >
              <Sparkles size={16} />
              {starting ? "Opening…" : "Begin"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoBlock({ title, body }) {
  return (
    <section className="rounded-2xl border border-white/5 bg-white/[0.02] p-4">
      <div className="text-xs uppercase tracking-[0.25em] text-neutral-500 mb-2">{title}</div>
      <p className="text-sm text-neutral-200 leading-relaxed whitespace-pre-wrap">{body}</p>
    </section>
  );
}

function StyleOption({ active, onSelect, disabled, title, desc, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onSelect}
      className={`w-full text-left rounded-xl border p-3 transition ${
        active
          ? "border-violet-500/50 bg-violet-500/10"
          : disabled
          ? "border-white/5 bg-white/[0.02] opacity-50"
          : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"
      }`}
    >
      <div className="font-medium text-white flex items-center">{title}</div>
      <div className="mt-0.5 text-xs text-neutral-400">{desc}</div>
    </button>
  );
}
